import { useState } from "react";
import LoginForm from "@/components/forms/loginForm";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Background from "@/icons/background";
import { renderToStaticMarkup } from "react-dom/server";
import "@/components/styles/login.css";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Modal,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import styles from "@/routes/login.module.css";
import { NavLink, useParams, useSearchParams } from "react-router-dom";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";

interface Props {
  subdomain: string;
}

export default function Login(props: Props) {
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { step } = useParams();

  const handleErrorMessage = (errorCode: string) => {
    setError(errorCode);
  };
  const handleResetErrorMessage = () => {
    setError(null);
  };
  useModalDismiss(!!error, handleResetErrorMessage);
  const svgString = encodeURIComponent(renderToStaticMarkup(<Background />));

  const backgroundImage = {
    background: `url('data:image/svg+xml;utf8, ${svgString}')`,
  } as React.CSSProperties;

  const handleErrorElement = () => {
    if (error === "invalid") {
      return (
        <div className={styles.errorContainer}>
          <ErrorOutlineIcon />
          <div className={styles.errorMessageWrapper}>
            <span className={styles.errorMessageSpan}>
              {t("2fa_invalid_link")}
            </span>
            <span className={styles.errorMessageSpan}>
              {t("2fa_invalid_link_message")}
            </span>
            <NavLink className={styles.navLink} to={"/login"}>
              {t("login")}
            </NavLink>
          </div>
        </div>
      );
    } else
      return (
        <Card className={styles.errorContainer}>
          <CardHeader
            avatar={<Avatar sx={{ bgcolor: "red" }}>!</Avatar>}
            titleTypographyProps={{
              component: "div",
              variant: "h6",
              fontFamily: "inherit",
            }}
            title={t("login_not_possible")}
          />
          <CardContent>
            <span className={styles.errorMessageSpan}>
              {error && (
                <span className={styles.errorMessageSpan}>{t(error)}</span>
              )}{" "}
            </span>
            <br />
            <br />

            <span className={styles.errorMessageSpan}>
              {t("for_questions_contact")} {""}
              <a
                className={styles.emailStyle}
                href="mailto:service@kaerhealth.com"
              >
                {" "}
                service@kaerhealth.com
              </a>
            </span>
            <br />
            <br />
          </CardContent>
          <CardActions>
            <NavLink className={styles.navLink} to={"/login"}>
              {t("login")}
            </NavLink>{" "}
          </CardActions>
        </Card>
      );
  };

  function portalName() {
    switch (props.subdomain) {
      case "admin":
        return t("kaer_staff_portal");
      case "manage":
        return t("kaer_manager_portal");
      case "portal":
        return t("kaer_employee_portal");
    }
  }

  const loginWrapper = {
    width: useMediaQuery(theme.breakpoints.up("sm")) ? "50%" : "80%",
    margin: useMediaQuery(theme.breakpoints.up("sm")) ? "0" : "2rem 0",
  } as React.CSSProperties;

  return (
    <div
      className={styles.mainContainer}
      style={backgroundImage}
      onClick={handleResetErrorMessage}
    >
      <div style={loginWrapper}>
        {!step && !error && (
          <LoginForm
            errorHandler={handleErrorMessage}
            errorReset={handleResetErrorMessage}
            subdomain={props.subdomain}
            portalName={portalName}
            token={token}
          />
        )}
        {step === "2fa" && (
          <div className={styles.infoContainer}>
            <h1 className={styles.infoTitle}>{t("login")}</h1>
            <p> {t("2fa_infotext_user_valid_login")}</p>
          </div>
        )}
      </div>
      <Modal
        slotProps={{
          backdrop: {
            onClick: (event) => event.stopPropagation(),
          },
        }}
        className={styles.modalContainer}
        open={!!error}
        onClose={handleResetErrorMessage}
      >
        {handleErrorElement()}
      </Modal>
    </div>
  );
}
